// Library methods
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import TextInputField from "src/components/shared/inputs/TextInputField/TextInputField";
import WizardSteps from "../WizardSteps/WizardSteps";
import FormField from "../FormField/FormField";
import ChoiceCard from "../ChoiceCard/ChoiceCard";
import InfoNote from "../InfoNote/InfoNote";
import WorklistTiming from "../WorklistTiming/WorklistTiming";
import ClinicField from "../ClinicField/ClinicField";

// Utils
import {
  DEFAULT_WORKLIST_TIMING,
  WorklistTimingValues,
  buildWorklistTiming,
  describeWorklistTiming,
} from "src/utils/emr/worklistTimingHelper";

// Hooks
import { useCreateEMRHubConnection } from "src/services/emr/hooks/emr-connections/useCreateEMRHubConnection";

// Types
import { EMRHubType } from "src/services/emr/types";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

export type FolderContent = "ExamFiles" | "WorklistCsv";

type AddSharedFolderModalType = {
  open: boolean;
  clinicId: string | undefined;
  hubId: string | undefined;
  hub?: EMRHubType;
  sharedFolderPaths: Array<string> | undefined;
  fixedContent?: FolderContent;
  title?: string;
  leadingSteps?: Array<string>;
  onBack?: () => void;
  onClose: () => void;
};

const AddSharedFolderModal = ({
  open,
  clinicId,
  hubId,
  hub,
  sharedFolderPaths,
  fixedContent,
  title,
  leadingSteps = [],
  onBack,
  onClose,
}: AddSharedFolderModalType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);

  const [step, setStep] = useState(0);
  const [content, setContent] = useState<FolderContent>(
    fixedContent ?? "ExamFiles"
  );
  const [path, setPath] = useState("");
  const [name, setName] = useState("");
  const [timing, setTiming] = useState<WorklistTimingValues>(
    DEFAULT_WORKLIST_TIMING
  );

  const { mutate, isPending } = useCreateEMRHubConnection(clinicId, hubId);

  const isWorklist = content === "WorklistCsv";
  const ownSteps = isWorklist
    ? [t("Folder"), t("Worklist timing")]
    : [t("Folder")];
  const steps = [...leadingSteps, ...ownSteps];
  const offset = leadingSteps.length;
  const trimmedPath = path.trim();
  const isDuplicatePath = Boolean(sharedFolderPaths?.includes(trimmedPath));
  const schedule = useMemo(() => buildWorklistTiming(timing), [timing]);

  const isFolderValid =
    trimmedPath.length > 0 && name.trim().length > 0 && !isDuplicatePath;
  const isLastStep = step === ownSteps.length - 1;

  const handleClose = useCallback(() => {
    setStep(0);
    setContent(fixedContent ?? "ExamFiles");
    setPath("");
    setName("");
    setTiming(DEFAULT_WORKLIST_TIMING);
    onClose();
  }, [fixedContent, onClose]);

  const handleCreate = () => {
    if (!clinicId || !isFolderValid) return;
    if (isWorklist && !schedule) return;

    mutate(
      {
        clinicId,
        name: name.trim(),
        sourceKind: isWorklist ? "Worklist" : "SharedFolder",
        direction: "Inbound",
        configJson: isWorklist
          ? { mode: "CsvFolder", path: trimmedPath, ...schedule }
          : { path: trimmedPath },
        enabled: true,
        deviceKey: null,
      },
      { onSuccess: handleClose }
    );
  };

  return (
    <DefaultModal
      isOpen={open}
      onClose={handleClose}
      title={title ?? t("Add a shared folder")}
      maxWidth="sm"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: "8px",
            width: "100%",
          }}
        >
          <Button
            sx={ModalCustomCancel}
            onClick={step > 0 ? () => setStep(step - 1) : onBack ?? handleClose}
          >
            {step === 0 && !onBack ? t("Cancel") : t("Back")}
          </Button>
          {isLastStep ? (
            <Button
              sx={ModalCustomConfirm}
              disabled={
                !isFolderValid || (isWorklist && !schedule) || isPending
              }
              onClick={handleCreate}
            >
              {isWorklist ? t("Create worklist") : t("Create shared folder")}
            </Button>
          ) : (
            <Button
              sx={ModalCustomConfirm}
              disabled={!isFolderValid}
              onClick={() => setStep(step + 1)}
            >
              {t("Next")}
            </Button>
          )}
        </Grid>
      }
    >
      <WizardSteps steps={steps} current={step + offset} />
      <Box display="flex" flexDirection="column" gap="16px" p="16px">
        {step === 0 && (
          <>
            {!fixedContent && (
              <Box display="flex" flexDirection="column" gap="8px">
                <Typography fontSize="15px" fontWeight={800}>
                  {t("What does this folder hold?")}
                </Typography>
                <ChoiceCard
                  name="add-shared-folder-content"
                  value="ExamFiles"
                  checked={content === "ExamFiles"}
                  title={t("Exam files")}
                  description={t(
                    "PDF, DICOM or XML exports from any machine, or test files. Picked up seconds after they land; the exam type is read from each file."
                  )}
                  onSelect={() => setContent("ExamFiles")}
                />
                <ChoiceCard
                  name="add-shared-folder-content"
                  value="WorklistCsv"
                  checked={content === "WorklistCsv"}
                  title={t("A worklist (CSV)")}
                  description={t(
                    "The EMR's daily appointments, one file per day named by date (2026-10-16.csv). Next step: how often to read it."
                  )}
                  onSelect={() => setContent("WorklistCsv")}
                />
              </Box>
            )}
            <ClinicField
              idPrefix="add-shared-folder"
              hub={hub}
              clinicId={clinicId}
            />
            <FormField
              label={t("Folder")}
              htmlFor="add-shared-folder-path-input"
              required
            >
              <TextInputField
                id="add-shared-folder-path-input"
                placeholder={
                  isWorklist ? "\\\\emr01\\worklist" : "C:\\testFolder\\test"
                }
                value={path}
                maxLength={260}
                error={isDuplicatePath}
                helperText={
                  isDuplicatePath
                    ? t("An EMR connection already exists for this path.")
                    : t(
                        "A local folder on the hub PC or a network share. The hub reads it with its own Windows account."
                      )
                }
                onChange={(event) => setPath(event.target.value)}
              />
            </FormField>
            <FormField
              label={t("Name")}
              htmlFor="add-shared-folder-name-input"
              required
            >
              <TextInputField
                id="add-shared-folder-name-input"
                placeholder={
                  isWorklist ? t("e.g. EMR worklist") : t("e.g. Test exams")
                }
                value={name}
                maxLength={100}
                onChange={(event) => setName(event.target.value)}
              />
            </FormField>
            {!isWorklist && (
              <InfoNote>
                {t(
                  "No device to pick. Exam files from a folder are not filtered by the worklist: everything that lands there is brought in."
                )}
              </InfoNote>
            )}
          </>
        )}

        {step === 1 && isWorklist && (
          <>
            <Box>
              <Typography fontSize="15px" fontWeight={800}>
                {t("When should the worklist be read?")}
              </Typography>
              <Typography fontSize="12px" color={mutedText}>
                {t(
                  "PAC server exams are fetched only for patients on the worklist, so keeping it current means same-day bookings get their exams too."
                )}
              </Typography>
            </Box>
            <WorklistTiming
              idPrefix="add-shared-folder"
              value={timing}
              onChange={setTiming}
            />
            {schedule && (
              <InfoNote testId="add-shared-folder-summary">
                {t(
                  "The hub reads today's worklist file from {{path}}: {{timing}}.",
                  {
                    path: trimmedPath,
                    timing: describeWorklistTiming(schedule, t).toLowerCase(),
                  }
                )}
              </InfoNote>
            )}
          </>
        )}
      </Box>
    </DefaultModal>
  );
};

export default AddSharedFolderModal;
